// Every hour here is read off the ontology's sessions; nothing in the browser knows a market's hours.
import {
  CALENDAR_WALK_DAYS,
  isWeekend,
  KEEP_LABEL,
  type MarketCalendar,
  nextOpen,
  PHASE_LABEL,
  phaseAt,
  type Session,
  type SessionPhase,
  usDaylight,
  type Venue,
} from '@alphadesk/ontology/calendar.ts';
import { firstDay, hoursOf, newsOf, normalOf } from '@alphadesk/ontology/calendar-said.ts';
import {
  actualSaid,
  aheadSaid,
  type EconomicEvent,
  eventSaid,
  eventTitle,
  type Importance,
  INDIA,
  isKey,
  keyEventsAhead,
  previousSaid,
} from '@alphadesk/ontology/economic.ts';
import { addDays, isoDayOf, istClock, todayInIst, weekdayOf } from '@alphadesk/ontology/when.ts';
import {
  API_PATHS,
  type CalendarRead,
  calendarSchema,
  type Line,
} from '@alphadesk/web/contract.ts';
import { fmtDivider, spanSaid } from './format.ts';
import { useRead } from './reads.ts';

export const DESK_VENUES: readonly Venue[] = ['NFO', 'MCX', 'NSE'];

const CALENDAR_POLL_MS = 5 * 60 * 1000;

const YEAR_WALK_DAYS = 366;

export function useCalendar(on = true) {
  return useRead(on ? API_PATHS.calendar : null, calendarSchema, { every: CALENDAR_POLL_MS });
}

export const namesOf = (calendar: MarketCalendar, venues: readonly Venue[], day: string) => [
  ...new Set(venues.flatMap((venue) => calendar.kept(venue, day)?.name ?? [])),
];

const inSaid = (at: Date, now: Date): string =>
  at.getTime() - now.getTime() < 60_000 ? 'in under a minute' : `in ${spanSaid(now, at)}`;

export interface VenueNow {
  venue: Venue;
  session: Session | null;
  phase: SessionPhase | null;
  said: string;
  why: string | null;
  next: string;
}

function whyOf(calendar: MarketCalendar, venue: Venue, day: string): string | null {
  const news = newsOf(calendar, venue, day);
  if (news) return `${news.name} — ${KEEP_LABEL[news.keep].toLowerCase()}`;
  return isWeekend(day) && calendar.session(venue, day) === null ? 'Weekend' : null;
}

function nextSaid(
  calendar: MarketCalendar,
  venue: Venue,
  session: Session | null,
  phase: SessionPhase | null,
  now: Date,
): string {
  if (phase?.phase === 'NORMAL') return `closes ${istClock(phase.to)} · ${inSaid(phase.to, now)}`;
  const later = session && normalOf(session).find((one) => one.from > now);
  if (later) return `opens ${istClock(later.from)} · ${inSaid(later.from, now)}`;
  const next = nextOpen(calendar, venue, now);
  return next
    ? `opens ${fmtDivider(next.open.toISOString(), todayInIst(now))} ${istClock(next.open)}`
    : `not open in the next ${CALENDAR_WALK_DAYS} days`;
}

export function venueNow(calendar: MarketCalendar, venue: Venue, now: Date): VenueNow {
  const today = todayInIst(now);
  const session = calendar.session(venue, today);
  const phase = phaseAt(session, now);
  return {
    venue,
    session,
    phase,
    said: phase ? PHASE_LABEL[phase.phase] : 'Shut',
    why: whyOf(calendar, venue, today),
    next: nextSaid(calendar, venue, session, phase, now),
  };
}

export type DayMark = 'open' | 'shut' | 'evening' | 'morning' | 'special' | 'unannounced';

export interface VenueDay {
  venue: Venue;
  mark: DayMark;
  said: string;
  reference: string | null;
}

export function dayOf(calendar: MarketCalendar, venue: Venue, day: string): VenueDay {
  const kept = calendar.kept(venue, day);
  const session = calendar.session(venue, day);
  const shown = (mark: DayMark, said: string): VenueDay => ({
    venue,
    mark,
    said,
    reference: kept?.reference ?? null,
  });
  if (kept?.keep === 'SPECIAL') {
    return session
      ? shown('special', `Special ${hoursOf(session)}`)
      : shown('unannounced', 'Special · hours not yet announced');
  }
  if (session === null) return shown('shut', 'Shut');
  if (kept?.keep === 'EVENING_ONLY') return shown('evening', `Evening only ${hoursOf(session)}`);
  if (kept?.keep === 'MORNING_ONLY') return shown('morning', `Morning only ${hoursOf(session)}`);
  return shown('open', 'Open');
}

interface ComingDay {
  day: string;
  names: string[];
  venues: VenueDay[];
  /** A weekend nothing trades on anyway: a holiday that costs no session. */
  idle: boolean;
}

export function comingUp(calendar: MarketCalendar, from: string, days: number): ComingDay[] {
  const until = addDays(from, days);
  const dates = [
    ...new Set(
      calendar
        .days()
        .filter((one) => one.day >= from && one.day < until)
        .map((one) => one.day),
    ),
  ].sort();
  return dates.map((day) => {
    const venues = DESK_VENUES.map((venue) => dayOf(calendar, venue, day));
    return {
      day,
      names: namesOf(calendar, DESK_VENUES, day),
      venues,
      idle: isWeekend(day) && venues.every((one) => one.mark === 'shut'),
    };
  });
}

export const WEEKDAYS = [
  ['M', 'Monday'],
  ['T', 'Tuesday'],
  ['W', 'Wednesday'],
  ['T', 'Thursday'],
  ['F', 'Friday'],
  ['S', 'Saturday'],
  ['S', 'Sunday'],
] as const;

export function monthDays(year: number, month: number): (string | null)[] {
  const first = isoDayOf(year, month, 1);
  const lead = (weekdayOf(first) + 6) % 7;
  const days: (string | null)[] = Array.from({ length: lead }, () => null);
  for (let day = first; day.startsWith(first.slice(0, 8)); day = addDays(day, 1)) days.push(day);
  return days;
}

function ordinarySession(calendar: MarketCalendar, venue: Venue, from: string): Session | null {
  const day = firstDay(from, (one) => !isWeekend(one) && calendar.kept(venue, one) === null);
  return day === null ? null : calendar.session(venue, day);
}

export const ordinaryHours = (calendar: MarketCalendar, today: string): Session[] =>
  DESK_VENUES.flatMap((venue) => ordinarySession(calendar, venue, today) ?? []);

export function mcxCloseSaid(calendar: MarketCalendar, today: string): string | null {
  const daylight = usDaylight(today);
  const now = ordinarySession(calendar, 'MCX', today);
  const last = addDays(today, YEAR_WALK_DAYS);
  let turn = addDays(today, 1);
  while (usDaylight(turn) === daylight && turn < last) turn = addDays(turn, 1);
  const then = ordinarySession(calendar, 'MCX', turn);
  if (now === null || then === null) return null;
  return (
    `MCX closes ${istClock(now.close)} while the US keeps ${daylight ? 'daylight' : 'standard'} time, ` +
    `and ${istClock(then.close)} from ${fmtDivider(then.open.toISOString(), today)}.`
  );
}

export function eventsLine(
  calendar: MarketCalendar,
  events: readonly EconomicEvent[],
  now: Date,
): string | null {
  const today = todayInIst(now);
  const ahead = keyEventsAhead(events, calendar, today);
  return ahead.length === 0 ? null : aheadSaid(ahead, today).join(' · ');
}

export interface CalendarEntry {
  day: string;
  /** `title` leaves the economy and the hour to the flag and the IST column; `said` names both. */
  said: Line;
  title: Line;
  economy: string;
  at: string | null;
  importance: Importance;
  key: boolean;
  previous: string | null;
  actual: string | null;
}

/** An expiry or a tender period's start is always key. */
export const calendarEntries = (read: CalendarRead): CalendarEntry[] =>
  [
    ...read.contracts.map(({ day, said }) => ({
      day,
      said,
      economy: INDIA,
      title: said,
      at: null,
      importance: 'HIGH' as const,
      key: true,
      previous: null,
      actual: null,
    })),
    ...read.events.map((event) => ({
      day: event.day,
      said: [eventSaid(event)],
      economy: event.economy,
      title: [eventTitle(event, false)],
      at: event.at === null ? null : istClock(event.at),
      importance: event.importance,
      key: isKey(event),
      previous: previousSaid(event),
      actual: actualSaid(event),
    })),
  ].sort((a, b) => a.day.localeCompare(b.day) || (a.at ?? '').localeCompare(b.at ?? ''));
